import { apiService } from './api';
import { Appointment, CreateAppointmentData, AudioUploadData } from '../types/appointment';

export interface AppointmentFilters {
  page?: number;
  limit?: number;
  status?: string;
  /** The standalone list's derived statuses, comma-separated (AMP-140). */
  listStatus?: string;
  providerId?: string;
  patientId?: string;
  dateFrom?: string;
  dateTo?: string;
  // New filter parameters
  search?: string;
  type?: string;
  recordingStatus?: string;
  transcriptionStatus?: string;
}

export interface AppointmentListResponse {
  appointments: Appointment[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export interface AppointmentResponse {
  appointment: Appointment;
  transcript?: {
    content: string;
    metadata: {
      duration?: number;
      language?: string;
      confidence?: number;
    };
    segments: Array<{
      start: number;
      end: number;
      text: string;
      confidence?: number;
    }>;
    clinicalInsights?: {
      chiefComplaint?: string;
      diagnosis?: string[];
      medications?: string[];
      followUp?: string;
    };
  };
}

class AppointmentService {
  private baseUrl = '/v1/appointments';

  /**
   * Get appointments with optional filtering
   */
  async getAppointments(filters: AppointmentFilters = {}): Promise<AppointmentListResponse> {
    const params = new URLSearchParams();

    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());
    if (filters.status) params.append('status', filters.status);
    if (filters.listStatus) params.append('listStatus', filters.listStatus);
    if (filters.providerId) params.append('providerId', filters.providerId);
    if (filters.patientId) params.append('patientId', filters.patientId);
    if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
    if (filters.dateTo) params.append('dateTo', filters.dateTo);
    // New filter parameters
    if (filters.search) params.append('search', filters.search);
    if (filters.type) params.append('type', filters.type);
    if (filters.recordingStatus) params.append('recordingStatus', filters.recordingStatus);
    if (filters.transcriptionStatus) params.append('transcriptionStatus', filters.transcriptionStatus);

    const queryString = params.toString();
    const url = queryString ? `${this.baseUrl}?${queryString}` : this.baseUrl;

    const response = await apiService.get<{ data: AppointmentListResponse }>(url, {});
    return response.data;
  }

  /**
   * Get specific appointment by ID
   */
  async getAppointment(appointmentId: string): Promise<AppointmentResponse> {
    const response = await apiService.get<{ data: AppointmentResponse }>(`${this.baseUrl}/${appointmentId}`);
    return response.data;
  }

  /**
   * Create new appointment
   */
  async createAppointment(data: CreateAppointmentData): Promise<Appointment> {
    const response = await apiService.post<{ data: { appointment: Appointment } }>(
      this.baseUrl,
      data
    );
    return response.data.appointment;
  }

  /**
   * Update appointment
   */
  async updateAppointment(appointmentId: string, data: Partial<CreateAppointmentData>): Promise<Appointment> {
    const response = await apiService.put<{ data: { appointment: Appointment } }>(
      `${this.baseUrl}/${appointmentId}`,
      data
    );
    return response.data.appointment;
  }

  /**
   * Delete appointment
   */
  /** Cancel a booked visit, mark it as a no-show, or put it back on the schedule. */
  async setVisitStatus(appointmentId: string, status: 'cancelled' | 'no-show' | 'scheduled', reason?: string): Promise<void> {
    await apiService.post(`${this.baseUrl}/${appointmentId}/visit-status`, { status, reason });
  }

  /** Everything the appointment details page shows, with the actions allowed to this user. */
  async getOverview(appointmentId: string): Promise<AppointmentOverview> {
    const response = await apiService.get<{ success: boolean; data: AppointmentOverview }>(
      `${this.baseUrl}/${appointmentId}/overview`
    );
    return response.data;
  }

  async deleteAppointment(appointmentId: string): Promise<void> {
    await apiService.delete<{ success: boolean; message: string }>(
      `${this.baseUrl}/${appointmentId}`
    );
  }

  /**
   * Upload audio file for transcription
   */
  async uploadAudio(
    appointmentId: string, 
    audioFile: File, 
    metadata: AudioUploadData = {}
  ): Promise<{ appointment: Partial<Appointment> }> {
    const formData = new FormData();
    formData.append('audio', audioFile);
    
    if (metadata.audioQuality) {
      formData.append('audioQuality', metadata.audioQuality);
    }
    
    if (metadata.deviceInfo) {
      formData.append('deviceInfo', JSON.stringify(metadata.deviceInfo));
    }

    // Use fetch for FormData to avoid Content-Type header issues
    const token = localStorage.getItem('accessToken');
    const fetchResponse = await fetch(`/api${this.baseUrl}/${appointmentId}/upload-audio`, {
      method: 'POST',
      headers: {
        'Authorization': token ? `Bearer ${token}` : '',
      },
      body: formData,
    });
    
    if (!fetchResponse.ok) {
      throw new Error(`Upload failed: ${fetchResponse.statusText}`);
    }
    
    const response = await fetchResponse.json();
    
    return response.data;
  }

  /**
   * Start recording for an appointment
   */
  async startRecording(appointmentId: string): Promise<Appointment> {
    const response = await apiService.put<{ data: { appointment: Appointment } }>(
      `${this.baseUrl}/${appointmentId}`,
      {
        recording: {
          status: 'recording'
        }
      }
    );
    return response.data.appointment;
  }

  /**
   * Stop recording for an appointment
   */
  async stopRecording(appointmentId: string): Promise<Appointment> {
    const response = await apiService.put<{ data: { appointment: Appointment } }>(
      `${this.baseUrl}/${appointmentId}`,
      {
        recording: {
          status: 'completed'
        }
      }
    );
    return response.data.appointment;
  }

  /**
   * Get appointments for a specific patient
   */
  async getPatientAppointments(patientId: string, filters: Omit<AppointmentFilters, 'patientId'> = {}): Promise<AppointmentListResponse> {
    return this.getAppointments({ ...filters, patientId });
  }

  /**
   * Get appointments for a specific provider
   */
  async getProviderAppointments(providerId: string, filters: Omit<AppointmentFilters, 'providerId'> = {}): Promise<AppointmentListResponse> {
    return this.getAppointments({ ...filters, providerId });
  }

  /**
   * Get recent appointments (last 30 days)
   */
  async getRecentAppointments(limit: number = 10): Promise<AppointmentListResponse> {
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    
    return this.getAppointments({
      limit,
      dateFrom: thirtyDaysAgo.toISOString().split('T')[0],
      dateTo: new Date().toISOString().split('T')[0]
    });
  }

  /**
   * Get upcoming appointments
   */
  async getUpcomingAppointments(limit: number = 10): Promise<AppointmentListResponse> {
    const today = new Date().toISOString().split('T')[0];
    
    return this.getAppointments({
      limit,
      dateFrom: today,
      status: 'pending'
    });
  }
}

export const appointmentService = new AppointmentService();

export interface AppointmentOverview {
  appointment: {
    id: string;
    scheduledDate: string;
    type: string;
    location: string;
    chiefComplaint?: string;
    recordingStatus: string;
    transcriptionStatus: string;
    createdAt?: string;
    visitStatus: 'scheduled' | 'cancelled' | 'no-show';
    visitStatusReason?: string;
    visitStatusChangedAt?: string;
  };
  patient: {
    id: string;
    firstName?: string;
    lastName?: string;
    mrn?: string;
    dateOfBirth?: string;
    gender?: string;
  } | null;
  provider: { id: string; name: string } | null;
  recordedBy: { id: string; name: string } | null;
  note: { state: 'none' | 'draft' | 'signed'; signedAt?: string; signedBy?: string };
  diagnoses: { code: string; description: string }[];
  actions: {
    canRecord: boolean;
    canEditDetails: boolean;
    canChangeProvider: boolean;
    canDelete: boolean;
    canOpenReview: boolean;
    canCancel: boolean;
    canMarkNoShow: boolean;
    canReinstate: boolean;
  };
}

